import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PageHeader from '../components/common/PageHeader';
import SEO from '../components/common/SEO';
import { getContentCreator, getAppUrls, getAppUrl } from '../utils/config';
import { useDirection } from '../contexts/DirectionContext';

const PrivacyPolicy: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { isRTL } = useDirection();
  const [creator, setCreator] = useState(getContentCreator());
  const [appUrls, setAppUrls] = useState(getAppUrls());
  const [appUrl, setAppUrl] = useState<string>('');

  useEffect(() => {
    // Update values if they change
    setCreator(getContentCreator());
    setAppUrls(getAppUrls());
    
    // Get the app URL based on platform
    const fetchAppUrl = async () => {
      try {
        const url = await getAppUrl();
        setAppUrl(url);
      } catch (error) {
        console.error('Error getting app URL:', error);
        setAppUrl(getAppUrls().website);
      }
    };
    
    fetchAppUrl();
  }, []);

  // Re-render when language changes
  useEffect(() => {
    // This effect ensures the component re-renders when language changes
  }, [i18n.language]);

  return (
    <div className="flex flex-col min-h-screen bg-background-primary">
      <SEO
        title={t('common.privacyPolicy')}
        description={t('common.privacyPolicyDescription')}
        keywords={[
          'Privacy Policy',
          'Data Protection',
          'User Privacy',
          'Personal Information',
          'Privacy Terms'
        ]}
      />
      <PageHeader
        title={t('common.privacyPolicy')}
        description={t('common.privacyPolicyDescription')}
      />
      
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="mt-8 prose prose-lg max-w-none ltr text-left" dir="ltr">
          <div className="text-text-secondary mb-8">
            <p className="font-medium">Last Updated: June 12, 2024</p>
          </div>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold mb-4 text-text-primary">1. Introduction</h2>
            <p>
              Welcome to the {creator.name} app ("the Service"). This Privacy Policy explains how we collect, use, and protect your information when you use our Service.
            </p>
            <p className="mt-4">
              {creator.organization} ("we," "us," or "our") is committed to protecting your privacy. This policy applies to all users of the Service, including web, iOS, and Android versions.
            </p>
            <p className="mt-4">
              By using the Service, you agree to the collection and use of information as described in this policy. If you do not agree, please do not use the Service.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold mb-4 text-text-primary">2. Information We Collect</h2>
            <p>
              We collect various types of information to provide and improve the Service. The specific data depends on how you interact with us:
            </p>
            
            <h3 className="text-xl font-medium mt-6 mb-3 text-text-primary">2.1 Personal Information</h3>
            <ul className="list-disc pl-6">
              <li><strong>Account Information:</strong> When you create an account (a planned feature), we will collect your name, email address, and account credentials (e.g., username and password).</li>
              <li><strong>Contact Information:</strong> If you reach out to us for support or inquiries, we may collect your name, email address, and any details you provide.</li>
              <li><strong>Payment Information:</strong> If you purchase subscriptions or premium features (future functionality), we collect payment details (e.g., credit card information) through secure third-party payment processors.</li>
            </ul>
            
            <h3 className="text-xl font-medium mt-6 mb-3 text-text-primary">2.2 Usage Data</h3>
            <ul className="list-disc pl-6">
              <li><strong>Activity Data:</strong> We collect information about your interactions with the Service, such as pages viewed, content watched, time spent, and features used.</li>
              <li><strong>Saved and Downloaded Content:</strong> Once implemented, saving or downloading content for offline viewing will involve collecting data about your preferences and downloaded items.</li>
            </ul>
            
            <h3 className="text-xl font-medium mt-6 mb-3 text-text-primary">2.3 Device and Technical Information</h3>
            <ul className="list-disc pl-6">
              <li><strong>Device Information:</strong> We gather details about your device, including IP address, browser type, operating system, device identifiers (e.g., IDFA or Android ID), and mobile network information.</li>
              <li><strong>Cookies and Tracking Technologies:</strong> We use cookies, web beacons, and similar technologies to enhance functionality, analyze usage, and personalize your experience. See our Cookie Policy (to be linked once created) for details.</li>
            </ul>
            
            <h3 className="text-xl font-medium mt-6 mb-3 text-text-primary">2.4 User-Generated Content</h3>
            <p>
              If future features allow comments, reviews, or other interactions, we will collect and store that content.
            </p>
            <p className="mt-4 italic">
              Note: We do not collect sensitive personal information (e.g., race, religion, health data) unless you voluntarily provide it. Currently, the Service focuses on {creator.name}'s content, with no user-generated content, but this may change with future updates.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold mb-4 text-text-primary">3. How We Use Your Information</h2>
            <p>
              We use your information for the following purposes:
            </p>
            <ul className="list-disc pl-6 mt-4">
              <li><strong>Service Delivery:</strong> To provide access to {creator.name}'s content, enable user login, facilitate saving and downloading content (once available), and ensure the Service operates smoothly.</li>
              <li><strong>Personalization:</strong> To tailor content recommendations based on your usage and preferences.</li>
              <li><strong>Communication:</strong> To send Service-related updates, respond to inquiries, and provide support.</li>
              <li><strong>Analytics:</strong> To analyze usage patterns and improve the Service's functionality and content.</li>
              <li><strong>Security:</strong> To detect and prevent fraud, unauthorized access, and other security threats.</li>
              <li><strong>Legal Compliance:</strong> To meet legal obligations, such as responding to lawful requests.</li>
            </ul>
            
            <h3 className="text-xl font-medium mt-6 mb-3 text-text-primary">Legal Basis (GDPR)</h3>
            <p>
              For EU users, our processing is based on:
            </p>
            <ul className="list-disc pl-6 mt-2">
              <li><strong>Contract:</strong> Providing the Service you request (e.g., account creation, content access).</li>
              <li><strong>Consent:</strong> Where you explicitly agree (e.g., cookies, optional features).</li>
              <li><strong>Legitimate Interests:</strong> For improving the Service and ensuring security, balanced against your rights.</li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold mb-4 text-text-primary">4. Sharing Your Information</h2>
            <p>
              We may share your information as follows:
            </p>
            
            <h3 className="text-xl font-medium mt-6 mb-3 text-text-primary">4.1 Service Providers</h3>
            <ul className="list-disc pl-6">
              <li><strong>Hosting:</strong> Third-party providers host our servers and store data.</li>
              <li><strong>Analytics:</strong> Tools like Google Analytics help us understand usage (data is anonymized where possible).</li>
              <li><strong>Payment Processors:</strong> Secure third parties process payments without us storing full payment details.</li>
            </ul>
            
            <h3 className="text-xl font-medium mt-6 mb-3 text-text-primary">4.2 Business Partners</h3>
            <p>
              Aggregated or anonymized data may be shared for business purposes (e.g., reporting to {creator.name}).
            </p>
            
            <h3 className="text-xl font-medium mt-6 mb-3 text-text-primary">4.3 Legal Authorities</h3>
            <p>
              We may disclose information if required by law, such as in response to a subpoena, court order, or regulatory demand.
            </p>
            
            <h3 className="text-xl font-medium mt-6 mb-3 text-text-primary">4.4 Corporate Transactions</h3>
            <p>
              If {creator.organization} is involved in a merger, acquisition, or asset sale, your information may be transferred to the new entity.
            </p>
            
            <p className="mt-4 font-medium">
              No Sale of Data: We do not sell your personal information to third parties.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold mb-4 text-text-primary">5. Data Security</h2>
            <p>
              We implement reasonable safeguards to protect your information, including:
            </p>
            <ul className="list-disc pl-6 mt-4">
              <li><strong>Encryption:</strong> Data in transit (e.g., via SSL/TLS) and at rest is encrypted.</li>
              <li><strong>Access Controls:</strong> Only authorized personnel can access personal data, under strict protocols.</li>
              <li><strong>Audits:</strong> We conduct regular security reviews and vulnerability assessments.</li>
              <li><strong>Incident Response:</strong> In case of a data breach, we will notify affected users and authorities as required by law (e.g., within 72 hours under GDPR).</li>
            </ul>
            <p className="mt-4 italic">
              Disclaimer: While we strive to protect your data, no system is 100% secure. We cannot guarantee absolute security but commit to taking all reasonable steps.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold mb-4 text-text-primary">6. Your Rights</h2>
            <p>
              You have rights over your personal information, depending on your location:
            </p>
            <ul className="list-disc pl-6 mt-4">
              <li><strong>Access:</strong> Request a copy of your data.</li>
              <li><strong>Correction:</strong> Fix inaccurate or incomplete information.</li>
              <li><strong>Deletion:</strong> Request removal of your data, subject to legal retention requirements.</li>
              <li><strong>Restriction:</strong> Limit how we process your data in certain cases.</li>
              <li><strong>Portability:</strong> Receive your data in a structured, machine-readable format.</li>
              <li><strong>Opt-Out:</strong> Opt out of marketing or data sharing (if applicable).</li>
              <li><strong>Withdraw Consent:</strong> Revoke consent where it applies.</li>
            </ul>
            <p className="mt-4">
              <strong>How to Exercise Your Rights:</strong> Contact us at {appUrls.privacyEmail}.
            </p>
            
            <h3 className="text-xl font-medium mt-6 mb-3 text-text-primary">Jurisdiction-Specific Rights</h3>
            <ul className="list-disc pl-6">
              <li><strong>EU (GDPR):</strong> Right to object to processing, lodge a complaint with a supervisory authority (e.g., your local Data Protection Authority).</li>
              <li><strong>California (CCPA):</strong> Right to opt out of data sales (we do not sell data) and request disclosure of data collected.</li>
              <li><strong>Canada (PIPEDA):</strong> Right to access and challenge the accuracy of your data.</li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold mb-4 text-text-primary">7. International Data Transfers</h2>
            <p>
              Your data may be transferred across borders due to our operations:
            </p>
            <ul className="list-disc pl-6 mt-4">
              <li><strong>{creator.country}:</strong> {creator.organization} processes and stores data under applicable privacy laws.</li>
              <li><strong>Global Users:</strong> Data may be processed in {creator.country} or other jurisdictions.</li>
            </ul>
            <p className="mt-4">
              For EU users, we ensure transfers meet GDPR standards using mechanisms like Standard Contractual Clauses. For all users, we maintain equivalent protection regardless of where data is processed.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold mb-4 text-text-primary">8. Children's Privacy</h2>
            <p>
              The Service is not intended for children under 16. We do not knowingly collect personal information from users under 16. If we discover such data has been collected without verifiable parental consent, we will delete it promptly. Parents or guardians who believe their child has provided data should contact us at {appUrls.privacyEmail}.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold mb-4 text-text-primary">9. Data Retention</h2>
            <p>
              We retain data only as long as necessary:
            </p>
            <ul className="list-disc pl-6 mt-4">
              <li><strong>Account Data:</strong> Kept while your account is active, plus 12 months after deletion for legal purposes.</li>
              <li><strong>Usage Data:</strong> Retained for up to 24 months for analytics.</li>
              <li><strong>Payment Data:</strong> Kept as required by tax laws (e.g., 7 years).</li>
            </ul>
            <p className="mt-4">
              You may request earlier deletion, subject to legal obligations.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold mb-4 text-text-primary">10. Cookies and Tracking Technologies</h2>
            <p>
              We use cookies and similar tools to:
            </p>
            <ul className="list-disc pl-6 mt-4">
              <li>Ensure functionality (e.g., session management).</li>
              <li>Analyze usage (e.g., page views).</li>
              <li>Personalize content.</li>
            </ul>
            <p className="mt-4">
              You can manage preferences via your browser or our Cookie Consent Tool (to be linked). EU users will be prompted for consent per the ePrivacy Directive.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold mb-4 text-text-primary">11. Changes to This Policy</h2>
            <p>
              We may update this policy as the Service evolves or laws change. Significant updates will be communicated via email or in-app notifications. The "Last Updated" date will reflect changes. Continued use after updates implies acceptance.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold mb-4 text-text-primary">12. Contact Us</h2>
            <p>
              For questions or to exercise your rights, reach out to:
            </p>
            <div className="mt-4 p-6 bg-surface-secondary rounded-lg border border-border-primary">
              <p className="font-semibold mb-2">{creator.organization}</p>
              <p>#203-1929 West Broadway</p>
              <p>Vancouver, British Columbia, Canada</p>
              <p>V6J 1Z3</p>
              <p className="mt-4"><strong>Email:</strong> {appUrls.privacyEmail}</p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy; 